import React, { Fragment, useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Select from "react-select";
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid, Legend,
} from "recharts";
import Breadcrumb from "../common/breadcrumb";
import { Container } from "reactstrap";
import { C, tbl, reportingGlobalCSS, selectStyles, fieldLabel, dateInput } from "./reportingStyles";
import { GradientDefs, ChartTooltip, Tile, RCard, Badge, StatusBadge, EventBadge, ChannelBadge, Loader, TablePagination } from "./ReportingShared";
import { DATE_PRESETS, ORDER_NATURE_OPTIONS, GRANULARITY_LABEL, daysAgo, ymdLocal } from "./reportingFilters";
import TopSpendersList from "./TopSpendersList";
import { getReportingCustomerLoyalty, getReportingCustomerLoyaltyCustomers, fetchBranches } from "../../utils/apiCalls";
import { isMerchantScoped } from "../../constants/roles";

const DEFAULT_SORT = { by: "totalSpent", dir: "desc" };

const CustomerLoyaltyPage = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const merchantIdFromStorage = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = isMerchantScoped(userType) ? merchantIdFromStorage : reduxSelectedMerchant;
  const hasMerchant = Boolean(selectedMerchant) && selectedMerchant !== "all";

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [subTab, setSubTab] = useState("customers");

  // ─── Filters ───────────────────────────────────────────
  const [datePreset, setDatePreset] = useState("30d");
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(ymdLocal());
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [orderNatures, setOrderNatures] = useState(ORDER_NATURE_OPTIONS);

  // ─── Customers table (server-side paged + sorted) ──────
  const [customers, setCustomers] = useState([]);
  const [custTotal, setCustTotal] = useState(0);
  const [custPage, setCustPage] = useState(1);
  const [custLimit, setCustLimit] = useState(20);
  const [custSort, setCustSort] = useState(DEFAULT_SORT);
  const [search, setSearch] = useState("");
  const [custLoading, setCustLoading] = useState(false);

  // Load branches when merchant changes
  useEffect(() => {
    if (!hasMerchant) return;
    setSelectedBranches([]);
    setCustSort(DEFAULT_SORT);
    (async () => {
      const res = await fetchBranches({ merchantId: selectedMerchant });
      const branches = res?.data || [];
      setBranchOptions(branches.map(b => ({ value: b.id, label: b.name })));
    })();
  }, [selectedMerchant, hasMerchant]);

  const datesReversed = Boolean(startDate && endDate && startDate > endDate);
  // Nothing to ask for: no order types, or a backwards range. Keep the last data on screen.
  const paused = orderNatures.length === 0 || datesReversed;

  const scopeFilters = useMemo(() => {
    const f = { orderNatures: orderNatures.map(o => o.value).join(",") };
    if (startDate) f.startDate = startDate;
    if (endDate) f.endDate = endDate;
    if (selectedBranches.length > 0) f.branchIds = selectedBranches.map(b => b.value).join(",");
    return f;
  }, [startDate, endDate, selectedBranches, orderNatures]);
  const scopeKey = JSON.stringify(scopeFilters);

  // Summary (tiles, charts, top spenders, tables). Debounced so toggling presets or
  // picking several branches sends one request; a stale response never overwrites a newer one.
  const summaryReq = useRef(0);
  useEffect(() => {
    if (!hasMerchant || paused) return;
    const t = setTimeout(async () => {
      const id = ++summaryReq.current;
      setLoading(true);
      try {
        const res = await getReportingCustomerLoyalty(selectedMerchant, scopeFilters);
        if (id === summaryReq.current && res) setData(res);
      } catch (e) { console.error("Failed to fetch customer loyalty data:", e); }
      if (id === summaryReq.current) setLoading(false);
    }, 500);
    return () => clearTimeout(t);
    // scopeKey stands in for scopeFilters so a new-but-equal object does not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMerchant, hasMerchant, paused, scopeKey]);

  // Any change to what the list shows starts it again from page 1.
  useEffect(() => { setCustPage(1); }, [selectedMerchant, scopeKey, custLimit, custSort, search]);

  const customersReq = useRef(0);
  useEffect(() => {
    if (!hasMerchant || paused) return;
    const t = setTimeout(async () => {
      const id = ++customersReq.current;
      setCustLoading(true);
      try {
        const res = await getReportingCustomerLoyaltyCustomers(selectedMerchant, scopeFilters, {
          page: custPage,
          limit: custLimit,
          sortBy: custSort.by,
          sortDir: custSort.dir,
          search: search.trim(),
        });
        if (id === customersReq.current && res) {
          setCustomers(res.customers || []);
          setCustTotal(res.meta?.total || 0);
        }
      } catch (e) { console.error("Failed to fetch customers:", e); }
      if (id === customersReq.current) setCustLoading(false);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMerchant, hasMerchant, paused, scopeKey, custPage, custLimit, custSort, search]);

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    const found = DATE_PRESETS.find(p => p.value === preset);
    if (found) {
      const [s, e] = found.range();
      setStartDate(s || "");
      setEndDate(e || "");
    }
  };

  const toggleSort = (key) => {
    setCustSort(prev => (prev.by === key ? { by: key, dir: prev.dir === "desc" ? "asc" : "desc" } : { by: key, dir: "desc" }));
  };

  const dailyVisits = data?.dailyVisits || [];
  const loyaltyTrend = data?.loyaltyTrend || [];
  const ledger = data?.ledger || [];
  const redeemedRewards = data?.redeemedRewards || [];
  const vouchers = data?.vouchers || [];
  const topSpenders = data?.topSpenders || [];
  const loyaltyType = data?.loyaltyType || "stamps";
  const walletPassStats = data?.walletPassStats || { generated: 0, installed: 0, activeDevices: 0, installRate: 0 };
  const installPct = walletPassStats.generated > 0 ? Math.round(walletPassStats.installRate * 100) : 0;
  const isStamps = loyaltyType === "stamps";

  // Describe what the data on screen was fetched with, not what the controls say now.
  const period = data?.period || {};
  const shownFilters = data?.filters || {};
  const filterActive = Boolean(shownFilters.active);
  const scopeFilterActive = (shownFilters.branchIds || []).length > 0 || (shownFilters.orderNatures || []).length < ORDER_NATURE_OPTIONS.length;
  const dateFilterActive = period.allTime === false;
  const gran = GRANULARITY_LABEL[period.granularity] || "daily";
  const periodLine = period.startDate
    ? `${period.allTime ? `All time · since ${period.startDate}` : `${period.startDate} → ${period.endDate}`}${period.timezone ? ` · ${period.timezone}` : ""}`
    : "";

  const lifetimeTotal = isStamps ? data?.loyaltyTotals?.lifetimeStamps ?? 0 : data?.loyaltyTotals?.lifetimePoints ?? 0;
  const savings = data?.rewardsSavings || { count: 0, amount: 0 };

  if (!hasMerchant) {
    return (
      <Fragment>
        <Breadcrumb title="Customer & Loyalty" parent="Reporting" />
        <Container fluid>
          <div className="rpt-wrap" style={{ padding: 40, textAlign: "center", color: C.textMed }}>
            <style>{reportingGlobalCSS}</style>
            Please select a merchant to view reporting data.
          </div>
        </Container>
      </Fragment>
    );
  }

  const SortTh = ({ label, sortKey }) => {
    const active = custSort.by === sortKey;
    return (
      <th
        style={{ ...tbl.th, cursor: "pointer", userSelect: "none", color: active ? C.coral : tbl.th.color }}
        onClick={() => toggleSort(sortKey)}
        aria-sort={active ? (custSort.dir === "asc" ? "ascending" : "descending") : "none"}
        title={filterActive && (sortKey === "totalOrders" || sortKey === "totalSpent") ? "In selected scope · click to sort" : "Click to sort"}
      >
        {label}
        <span style={{ marginLeft: 4, fontSize: 10, opacity: active ? 1 : 0.35 }}>
          {active ? (custSort.dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </th>
    );
  };
  const PlainTh = ({ label, title }) => <th style={tbl.th} title={title}>{label}</th>;

  return (
    <Fragment>
      <Breadcrumb title="Customer & Loyalty" parent="Reporting" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">

          {/* ─── Filter Bar ─────────────────────────────── */}
          <RCard style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
              <div style={{ minWidth: 140 }}>
                <div style={fieldLabel}>Date Range</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {DATE_PRESETS.map(p => (
                    <div key={p.value} onClick={() => handlePresetChange(p.value)}
                      style={{
                        padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: datePreset === p.value ? 700 : 500,
                        cursor: "pointer", border: datePreset === p.value ? `1.5px solid ${C.coral}` : `1.5px solid ${C.border}`,
                        background: datePreset === p.value ? C.coralLight : C.white,
                        color: datePreset === p.value ? C.coral : C.textMed, whiteSpace: "nowrap",
                      }}>{p.label}</div>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div>
                  <div style={fieldLabel}>From</div>
                  <input type="date" value={startDate} style={dateInput}
                    onChange={e => { setStartDate(e.target.value); setDatePreset("custom"); }} />
                </div>
                <div>
                  <div style={fieldLabel}>To</div>
                  <input type="date" value={endDate} style={dateInput}
                    onChange={e => { setEndDate(e.target.value); setDatePreset("custom"); }} />
                </div>
              </div>

              <div style={{ minWidth: 240, flex: 1, maxWidth: 400 }}>
                <div style={fieldLabel}>Branches</div>
                <Select
                  isMulti
                  options={branchOptions}
                  value={selectedBranches}
                  onChange={val => setSelectedBranches(val || [])}
                  placeholder="All Branches"
                  styles={selectStyles}
                  isClearable
                  closeMenuOnSelect={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                />
              </div>

              <div style={{ minWidth: 260, flex: 1, maxWidth: 440 }}>
                <div style={fieldLabel}>Order Types</div>
                <Select
                  isMulti
                  options={ORDER_NATURE_OPTIONS}
                  value={orderNatures}
                  onChange={val => setOrderNatures(val || [])}
                  placeholder="Select order types"
                  styles={selectStyles}
                  closeMenuOnSelect={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                />
              </div>
            </div>
            {orderNatures.length === 0 && (
              <div style={{ fontSize: 11, color: C.orange, fontWeight: 600, marginTop: 8 }}>
                Select at least one order type — report paused, showing last selection.
              </div>
            )}
            {datesReversed && (
              <div style={{ fontSize: 11, color: C.orange, fontWeight: 600, marginTop: 8 }}>
                "From" is after "To" — report paused, showing last selection.
              </div>
            )}
            <div style={{ fontSize: 11.5, color: C.textLight, marginTop: 10 }}>
              Branch and order-type filters apply to order-based figures (customers, top spenders, visits).
              The loyalty ledger, rewards and vouchers follow the date range only. Orders without a branch are excluded when branches are selected.
            </div>
          </RCard>

          {loading && !data ? <Loader /> : (
            <div style={{ opacity: loading ? 0.55 : 1, transition: "opacity 0.15s" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 24 }}>
                <Tile label="Total Customers" value={data?.totalCustomers ?? 0}
                  sub={filterActive ? `${data?.customersInScope ?? 0} ordered in selected scope` : `${data?.activeLast30 ?? 0} active in 30 days`}
                  icon="👥" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                <Tile label="Visits Today" value={data?.visitsToday ?? 0} sub={scopeFilterActive ? "Unique visitors · filtered" : "Unique visitors"} icon="📅" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={80} />
                <Tile label={isStamps ? "Total Stamps" : "Total Points"} value={lifetimeTotal} sub="Lifetime earned · all customers" icon="⭐" gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} delay={160} />
                <Tile label="Rewards Savings" value={`${savings.amount} SAR`} sub={`${savings.count} redeemed${dateFilterActive ? " in range" : ""}`} icon="🎁" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={240} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 24 }}>
                <Tile label="Passes Generated" value={walletPassStats.generated} sub="Cards downloaded by customers" icon="📥" gradient={`linear-gradient(135deg, ${C.blue}, #6FA8FF)`} delay={0} />
                <Tile label="Passes Installed" value={walletPassStats.installed} sub={`${installPct}% adoption rate`} icon="📲" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={80} />
                <Tile label="Active Devices" value={walletPassStats.activeDevices} sub="iPhones / iPads receiving updates" icon="🍎" gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`} delay={160} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 16, marginBottom: 20 }}>
                <RCard title={`📊 Unique Visitors (${gran})`}>
                  {periodLine && <div style={{ fontSize: 11, color: C.textLight, marginTop: -10, marginBottom: 10 }}>{periodLine}</div>}
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={dailyVisits}>
                      <GradientDefs />
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: C.textMed }} axisLine={{ stroke: C.border }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Area type="monotone" dataKey="returning" stackId="1" stroke="#667EEA" fill="url(#gBlue)" strokeWidth={2} name="Returning" />
                      <Area type="monotone" dataKey="newCustomers" stackId="1" stroke="#009689" fill="url(#gCoral)" strokeWidth={2} name="New" />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </RCard>
                <RCard title="🏆 Top Spenders (SAR)">
                  <TopSpendersList spenders={topSpenders} onSelect={(id) => navigate(`/reporting/customer-detail/${id}`)} />
                </RCard>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 16, marginBottom: 20 }}>
                <RCard title={`${isStamps ? "⭐ Stamps" : "⭐ Points"} Earned vs Redeemed (${gran})`}>
                  {periodLine && <div style={{ fontSize: 11, color: C.textLight, marginTop: -10, marginBottom: 10 }}>{periodLine}</div>}
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={loyaltyTrend}>
                      <GradientDefs />
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: C.textMed }} axisLine={{ stroke: C.border }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Area type="monotone" dataKey="earned" stroke="#2ECC71" fill="url(#gGreen)" strokeWidth={2.5} name="Earned" dot={{ r: 3, fill: "#2ECC71", strokeWidth: 0 }} />
                      <Area type="monotone" dataKey="redeemed" stroke="#009689" fill="url(#gCoral)" strokeWidth={2.5} name="Redeemed" dot={{ r: 3, fill: "#009689", strokeWidth: 0 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </RCard>
                <RCard title="🎫 Voucher Status">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={[
                        { name: "Active", value: vouchers.filter(v => v.status === "Active").length },
                        { name: "Redeemed", value: vouchers.filter(v => v.status === "Redeemed").length },
                        { name: "Expired", value: vouchers.filter(v => v.status === "Expired").length },
                      ]} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value"
                        label={({ name, value, cx, x, y }) => (<text x={x} y={y} fill={C.text} fontSize={12} fontWeight={600} textAnchor={x > cx ? "start" : "end"} dominantBaseline="central">{`${name} (${value})`}</text>)}
                        paddingAngle={3} cornerRadius={4}>
                        <Cell fill={C.green} /><Cell fill={C.orange} /><Cell fill={C.coral} />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </RCard>
              </div>

              {/* Tabs */}
              <RCard>
                <div style={{ display: "flex", gap: 0, borderBottom: `2px solid ${C.borderLight}`, marginBottom: 16, overflowX: "auto" }}>
                  {[["customers", "Customers"], ["ledger", "Loyalty Ledger"], ["redeemed", "Redeemed Rewards"], ["vouchers", "Vouchers"]].map(([k, l]) => (
                    <div key={k} onClick={() => setSubTab(k)} style={{ padding: "10px 20px", cursor: "pointer", fontWeight: subTab === k ? 700 : 500, color: subTab === k ? C.coral : C.textMed, borderBottom: subTab === k ? `3px solid ${C.coral}` : "3px solid transparent", fontSize: 13, transition: "all 0.15s", whiteSpace: "nowrap" }}>{l}</div>
                  ))}
                </div>

                {subTab === "customers" && <>
                  <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ position: "relative" }}>
                      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone or ID..."
                        style={{ padding: "9px 14px 9px 36px", borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, width: 280, outline: "none", fontFamily: "inherit", transition: "border 0.15s" }}
                        onFocus={e => e.target.style.borderColor = C.coral} onBlur={e => e.target.style.borderColor = C.border} />
                      <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, opacity: 0.4 }}>🔍</span>
                    </div>
                    {filterActive && (
                      <span style={{ fontSize: 12, color: C.textMed }}>Showing customers who ordered in the selected scope.</span>
                    )}
                    <span style={{ marginLeft: "auto", fontSize: 12, color: C.textLight, background: C.bg, padding: "4px 12px", borderRadius: 20 }}>{custTotal} results</span>
                  </div>
                  <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}`, opacity: custLoading ? 0.55 : 1, transition: "opacity 0.15s" }}>
                    <table style={tbl.table}>
                      <thead>
                        <tr>
                          <PlainTh label="ID" />
                          <PlainTh label="User Name" />
                          <PlainTh label="Phone" />
                          <PlainTh label="Status" />
                          <SortTh label={isStamps ? "Lifetime Stamps" : "Lifetime Points"} sortKey={isStamps ? "lifetimeStamps" : "lifetimePoints"} />
                          <SortTh label={isStamps ? "Stamps Balance" : "Points Balance"} sortKey={isStamps ? "stampsBalance" : "pointsBalance"} />
                          <SortTh label="Lifetime Rewards" sortKey="lifetimeRewards" />
                          <SortTh label="Rewards Balance" sortKey="rewardsBalance" />
                          <SortTh label="Orders" sortKey="totalOrders" />
                          <SortTh label="Total Spent" sortKey="totalSpent" />
                          <PlainTh label="Fav Branch" title={filterActive ? "In selected scope" : undefined} />
                          <PlainTh label="Last Visit" title={filterActive ? "In selected scope" : undefined} />
                        </tr>
                      </thead>
                      <tbody>
                        {customers.map((c, i) => (
                          <tr key={c.id} className="rpt-row" style={{ cursor: "pointer", animationDelay: `${i * 30}ms` }}
                            onClick={() => navigate(`/reporting/customer-detail/${c.id}`)}>
                            <td style={tbl.td}>{c.id}</td>
                            <td style={{ ...tbl.td, fontWeight: 700, color: C.text }}>{c.name}</td>
                            <td style={{ ...tbl.td, color: C.textMed, whiteSpace: "nowrap" }}>{c.phone || "—"}</td>
                            <td style={tbl.td}>
                              {c.active === false
                                ? <Badge color={C.coral} bg={C.coralLight}>Inactive</Badge>
                                : <Badge color={C.green} bg={C.greenLight}>Active</Badge>}
                            </td>
                            <td style={tbl.td}>{isStamps ? c.lifetimeStamps : c.lifetimePoints}</td>
                            <td style={{ ...tbl.td, fontWeight: 600 }}>{isStamps ? c.stampsBalance : c.pointsBalance}</td>
                            <td style={tbl.td}>{c.lifetimeRewards}</td>
                            <td style={{ ...tbl.td, fontWeight: 600 }}>{c.rewardsBalance}</td>
                            <td style={tbl.td}>{c.totalOrders}</td>
                            <td style={{ ...tbl.td, fontWeight: 700, color: C.text }}>{c.totalSpent} SAR</td>
                            <td style={tbl.td}>{c.favBranch}</td>
                            <td style={{ ...tbl.td, color: C.textLight }}>{c.lastVisit || "—"}</td>
                          </tr>
                        ))}
                        {!custLoading && customers.length === 0 && (
                          <tr><td colSpan={12} style={{ ...tbl.td, textAlign: "center", color: C.textLight, padding: 24 }}>No customers match.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <TablePagination total={custTotal} page={custPage} limit={custLimit} onPageChange={setCustPage} onLimitChange={setCustLimit} />
                </>}

                {subTab === "ledger" && <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                  <table style={tbl.table}>
                    <thead><tr>{["ID", "Date", "User", "Merchant", "Order ID", "Event Type", "Points/Stamps", "Description"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
                    <tbody>{ledger.map(l => (
                      <tr key={l.id} className="rpt-row">
                        <td style={{ ...tbl.td, color: C.textLight }}>{l.id}</td>
                        <td style={tbl.td}>{l.date}</td>
                        <td style={{ ...tbl.td, fontWeight: 700 }}>{l.user}</td>
                        <td style={tbl.td}>{l.merchantName}</td>
                        <td style={{ ...tbl.td, color: C.coral, fontWeight: 700 }}>{l.orderId || "N/A"}</td>
                        <td style={tbl.td}><EventBadge t={l.type} /></td>
                        <td style={{ ...tbl.td, fontWeight: 700, color: String(l.delta).startsWith("-") ? C.coral : String(l.delta) === "0" ? C.textLight : C.green }}>{l.delta}</td>
                        <td style={{ ...tbl.td, color: C.textMed }}>{l.desc}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>}

                {subTab === "redeemed" && <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                  <table style={tbl.table}>
                    <thead><tr>{["ID", "Code", "User", "Merchant", "Product", "Price", "Channel", "Type"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
                    <tbody>{redeemedRewards.map(r => (
                      <tr key={r.id} className="rpt-row">
                        <td style={tbl.td}>{r.id}</td>
                        <td style={{ ...tbl.td, fontFamily: "monospace", fontSize: 12, color: C.textMed }}>{r.code}</td>
                        <td style={{ ...tbl.td, fontWeight: 700 }}>{r.user}</td>
                        <td style={tbl.td}>{r.merchantName}</td>
                        <td style={tbl.td}>{r.product}</td>
                        <td style={{ ...tbl.td, fontWeight: 700 }}>{r.price} SAR</td>
                        <td style={tbl.td}><ChannelBadge c={r.channel} /></td>
                        <td style={{ ...tbl.td, color: C.textMed }}>{r.rewardType}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>}

                {subTab === "vouchers" && <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                  <table style={tbl.table}>
                    <thead><tr>{["Code", "Merchant", "User", "Product", "Status", "Redeemed At", "Expires At"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
                    <tbody>{vouchers.map(v => (
                      <tr key={v.code} className="rpt-row">
                        <td style={{ ...tbl.td, fontFamily: "monospace", fontSize: 12, color: C.textMed }}>{v.code}</td>
                        <td style={tbl.td}>{v.merchantName}</td>
                        <td style={{ ...tbl.td, fontWeight: 700 }}>{v.user}</td>
                        <td style={tbl.td}>{v.product}</td>
                        <td style={tbl.td}><StatusBadge s={v.status} /></td>
                        <td style={tbl.td}>{v.redeemedAt || "-"}</td>
                        <td style={{ ...tbl.td, color: C.textLight }}>{v.expiresAt}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>}
              </RCard>
            </div>
          )}
        </div>
      </Container>
    </Fragment>
  );
};

export default CustomerLoyaltyPage;
